import { useCallback } from "react";
import useFormStore from "@state/FormStore";
import { useBoat } from "@state/DataStore";
import useMessageStore from "@state/MessageStore";
import Alert from "@components/Alert";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import Input from "@components/Input";
import Button from "@components/Button";
import { useTranslation } from "react-i18next";
import { useAdmin } from "@utils/ModeContext";
import useAddReservation from "./use-add-reservation";

const From = () => {
  const isAdmin = useAdmin();
  const { t } = useTranslation();
  const { date, boatId } = useParams();
  const navigate = useNavigate();
  const formState = useFormStore((state) => state);

  if (boatId == null || date == null) {
    throw new Error(t<string>("No active reservation"));
  }
  const boat = useBoat(parseInt(boatId, 10));

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (
      name === "name" ||
      name === "phone" ||
      name === "email" ||
      name === "text"
    ) {
      useFormStore.getState().setField(name, value);
    }
  };

  const { loading, error, addReservation } = useAddReservation();

  const onBack = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const { name, phone, email, text } = useFormStore.getState();
      const id = await addReservation({
        name,
        phone,
        email,
        text,
        boatId: parseInt(boatId, 10),
        date: new Date(date),
      });
      if (id !== null) {
        useMessageStore
          .getState()
          .addMessage(t("reservationSuccessfullyCreated", { id }));
        onBack();
        useFormStore.getState().reset();
      }
    },
    [addReservation, boatId, date, onBack, t]
  );

  return (
    <div>
      <h2 className="text-4xl font-extrabold mb-4">
        {boat?.name ?? t("Unknown ship")}
        <small className="block font-semibold text-gray-500 dark:text-gray-400">
          {new Date(date).toLocaleDateString()} -{" "}
          {dayjs(new Date(date)).add(7, "day").toDate().toLocaleDateString()}
        </small>
      </h2>
      {error && <Alert type="error">{error}</Alert>}
      <form onSubmit={onSubmit}>
        <Input
          name="name"
          label={t("Name")}
          value={formState.name}
          onChange={onChange}
          required
        />
        <Input
          name="phone"
          label={t("Phone")}
          value={formState.phone}
          onChange={onChange}
          required={!isAdmin}
          pattern="^[0-9 +]*$"
        />
        <Input
          name="email"
          label={t("Email")}
          value={formState.email}
          onChange={onChange}
          required={!isAdmin}
          pattern="^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$"
        />
        <Input
          type="textarea"
          name="text"
          label={t("Note")}
          value={formState.text}
          onChange={onChange}
        />
        <div className="flex flex-row gap-4 justify-end">
          <Button importance="secondary" onClick={onBack}>
            {t<string>("Back")}
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            {t<string>("Book without obligation")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default From;
